import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Clock, User, ChevronRight, Brain, Zap, Heart, Activity, Stethoscope, Apple } from 'lucide-react';
import { useHeroImage } from '@/hooks/useHeroImage';
import HeroImageEditor from '@/components/HeroImageEditor';
import EditableText from '@/components/EditableText';
import EditableButton from '@/components/EditableButton';
import MailingListSignup from '@/components/MailingListSignup';
import PageQuote from '@/components/PageQuote';
import FeaturedAcupunctureCard from '@/components/FeaturedAcupunctureCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const [schedule, setSchedule] = useState(null);
  const [aboutPreview, setAboutPreview] = useState(null);
  const [contactButton, setContactButton] = useState(null);
  const [areas, setAreas] = useState([]);
  // Hardcoded hero image - works on both preview and production
  const heroImage = useHeroImage('home', 'https://customer-assets.emergentagent.com/job_acustudyai/artifacts/5do8vcwy_3644538-1729035778.jpg');
  const specializationsImage = useHeroImage('home_specializations');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [scheduleRes, aboutRes, contactButtonRes, areasRes] = await Promise.all([
        axios.get(`${API}/schedule`),
        axios.get(`${API}/about`),
        axios.get(`${API}/settings/contact-button`),
        axios.get(`${API}/areas`)
      ]);
      setSchedule(scheduleRes.data);
      setAboutPreview(aboutRes.data);
      setContactButton(contactButtonRes.data);
      setAreas(areasRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  // Helper function to get icon component based on slug
  const getIconForArea = (slug) => {
    const iconMap = {
      'mental-health': Brain,
      'sports-medicine': Zap,
      'reproductive-health': Heart,
      'pain-management': Activity,
      'general-care': Stethoscope,
      'nutrition-guidance': Apple
    };
    return iconMap[slug] || Activity; // Default to Activity icon if not found
  };

  // Helper function to get gradient colors based on slug
  const getGradientForArea = (slug) => {
    const gradientMap = {
      'mental-health': { from: '#E85D4D', to: '#E8934D', text: '#E85D4D' },
      'sports-medicine': { from: '#E8934D', to: '#4D9E7C', text: '#E8934D' },
      'reproductive-health': { from: '#E85D4D', to: '#4D9E7C', text: '#E85D4D' },
      'pain-management': { from: '#4D9E7C', to: '#E8934D', text: '#4D9E7C' },
      'general-care': { from: '#E8934D', to: '#E85D4D', text: '#E8934D' },
      'nutrition-guidance': { from: '#4D9E7C', to: '#E85D4D', text: '#4D9E7C' }
    };
    return gradientMap[slug] || { from: '#E8934D', to: '#E85D4D', text: '#E85D4D' };
  };

  return (
    <Layout>
      {/* Hero Section with Chicago Background */}
      <section className="relative vhs-effect overflow-hidden" data-testid="hero-section">
        <HeroImageEditor pageName="home" />
        {/* Iconic Chicago Skyline with Willis Tower */}
        <div className="absolute inset-0 z-0 min-h-[600px]">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-40"
            style={{
              backgroundImage: `url(${heroImage})`,
              filter: 'sepia(40%) saturate(120%) hue-rotate(-5deg) brightness(0.9)'
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#2C1810]/80 via-[#E85D4D]/10 to-[#2C1810]/80"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 min-h-[600px] flex items-center justify-center py-12">
          <div className="text-center space-y-6">
            <h1 className="text-6xl sm:text-7xl md:text-9xl retro-title leading-tight" data-testid="hero-title">
              <EditableText as="span" id="home.hero.title">{"Welcome to\nAcuErnie"}</EditableText>
            </h1>
            <div className="flex items-center justify-center space-x-4">
              <User className="w-8 h-8 text-[#E8934D]" />
              <p className="text-2xl md:text-3xl text-[#F4E4C1] font-bold" data-testid="hero-subtitle">
                <EditableText as="span" id="home.hero.subtitle">Doctoral Student & Senior Intern</EditableText>
              </p>
            </div>
            <p className="text-xl md:text-2xl text-[#F4E4C1] max-w-3xl mx-auto font-medium">
              <EditableText as="span" id="home.hero.tagline">Acupuncture • Student Resources • Internal Arts</EditableText>
            </p>
            <p className="text-xl text-[#E8934D] font-bold">
              <EditableText as="span" id="home.hero.location">📍 Chicago, IL</EditableText>
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 w-full max-w-2xl mx-auto px-4">
              <EditableButton
                id="home.cta.resources"
                defaultLabel="📚 Student Resources"
                defaultUrl="/student-resources"
                className="retro-button w-full sm:w-auto"
                testid="student-resources-button"
              />
              <EditableButton
                id="home.cta.testing"
                defaultLabel="📝 Testing Center - Exam Prep"
                defaultUrl="/testing-center"
                className="retro-button bg-[#E8934D] hover:bg-[#E85D4D] w-full sm:w-auto"
                testid="testing-center-button"
              />
            </div>
          </div>
        </div>
        
        {/* Decorative VHS lines */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#E8B67A] to-transparent opacity-30 z-20"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#E8B67A] to-transparent opacity-30 z-20"></div>
      </section>

      {/* Third Quote - Before Areas of Focus */}
      <PageQuote pageName="home" position={3} variant="compact" />

      {/* Featured Acupuncture Card */}
      <FeaturedAcupunctureCard />

      {/* Specializations Section - Moved Further Down */}
      <section className="py-24 relative" data-testid="specializations-section">
        <HeroImageEditor pageName="home_specializations" />
        {/* Acupuncture Needles Background */}
        <div className="absolute inset-0 opacity-10 h-[500px]">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${specializationsImage})`,
              filter: 'sepia(50%) saturate(120%)'
            }}
          ></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-5xl md:text-6xl retro-title mb-4"><EditableText as="span" id="home.areas.title">Areas of Focus</EditableText></h2>
            <p className="text-xl text-[#F4E4C1]"><EditableText as="span" id="home.areas.subtitle">Specialized Care for Your Wellness Journey</EditableText></p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {areas.length > 0 ? (
              areas.map((area) => {
                const IconComponent = getIconForArea(area.slug);
                const colors = getGradientForArea(area.slug);
                
                return (
                  <div 
                    key={area.id}
                    className="group cassette-card p-8 hover:scale-105 hover:shadow-2xl transition-all duration-300 cursor-pointer" 
                    data-testid={`focus-${area.slug}`}
                  >
                    <div className="text-center">
                      <div className="relative inline-flex items-center justify-center mb-6">
                        {area.icon_image_url ? (
                          <div className="relative">
                            <div 
                              className="absolute inset-0 rounded-3xl opacity-20 group-hover:opacity-30 group-hover:scale-105 transition-all duration-300 blur-sm"
                              style={{
                                background: `linear-gradient(to bottom right, ${colors.from}, ${colors.to})`
                              }}
                            ></div>
                            <img
                              src={area.icon_image_url}
                              alt={area.title}
                              className="w-48 h-48 object-cover rounded-3xl relative z-10 group-hover:scale-105 transition-all duration-300 shadow-lg"
                            />
                          </div>
                        ) : (
                          <div className="relative inline-flex items-center justify-center w-20 h-20">
                            <div 
                              className="absolute inset-0 rounded-2xl opacity-20 group-hover:opacity-30 group-hover:scale-110 transition-all duration-300"
                              style={{
                                background: `linear-gradient(to bottom right, ${colors.from}, ${colors.to})`
                              }}
                            ></div>
                            <IconComponent 
                              className="w-10 h-10 relative z-10 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300" 
                              style={{ color: colors.text }}
                            />
                          </div>
                        )}
                      </div>
                      <h3 
                        className="text-2xl retro-title text-[#2C1810] mb-4 group-hover:transition-colors"
                        style={{ '--hover-color': colors.text }}
                        onMouseEnter={(e) => e.target.style.color = colors.text}
                        onMouseLeave={(e) => e.target.style.color = '#2C1810'}
                      >
                        {area.title}
                      </h3>
                      <p className="text-[#2C1810] mb-6 text-sm leading-relaxed line-clamp-3">
                        {area.subtitle || area.description}
                      </p>
                      <a 
                        href={`/areas/${area.slug}`} 
                        className="inline-flex items-center gap-2 text-[#E85D4D] font-bold hover:text-[#E8934D] group-hover:gap-3 transition-all"
                      >
                        Learn More <ChevronRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                );
              })
            ) : (
              // Fallback while loading
              <div className="col-span-full text-center py-12">
                <div className="w-16 h-16 border-4 border-[#D4A574] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-[#F4E4C1]">Loading areas of focus...</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section className="py-16 bg-gradient-to-b from-[#2C1810] to-[#E85D4D]/20" data-testid="schedule-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl retro-title text-center mb-12"><EditableText as="span" id="home.schedule.title">Find Me At</EditableText></h2>
          {schedule ? (
            <div className="cassette-card max-w-2xl mx-auto p-8" data-testid="schedule-card">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 text-[#3D2817] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-2xl font-bold text-[#3D2817] mb-2" data-testid="clinic-name">{schedule.clinic_name}</h3>
                    <p className="text-[#3D2817]" data-testid="clinic-address">{schedule.address}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 pt-6 border-t-2 border-[#6B4423]">
                  <Clock className="w-6 h-6 text-[#3D2817] mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-bold text-[#3D2817] mb-2"><EditableText as="span" id="home.schedule.hours_label">Clinic Hours</EditableText></h3>
                    <p className="text-[#3D2817] whitespace-pre-wrap" data-testid="clinic-hours">{schedule.hours}</p>
                  </div>
                </div>
                
                {/* Contact Button */}
                {contactButton?.enabled && contactButton?.button_url && (
                  <div className="pt-6 border-t-2 border-[#6B4423] text-center">
                    <a
                      href={contactButton.button_url}
                      target={contactButton.button_url.startsWith('tel:') ? '_self' : '_blank'}
                      rel={contactButton.button_url.startsWith('tel:') ? undefined : 'noopener noreferrer'}
                      className="retro-button inline-flex items-center gap-2"
                    >
                      <User className="w-5 h-5" />
                      {contactButton.button_text || 'Contact Me'}
                    </a>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-center text-[#F4E4C1]">Schedule information coming soon...</p>
          )}
        </div>
      </section>

      {/* About Preview */}
      {aboutPreview && (
        <section className="py-16" data-testid="about-preview-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="admin-card">
              <h2 className="text-4xl md:text-5xl retro-title mb-6"><EditableText as="span" id="home.approach.title">My Approach</EditableText></h2>
              <p className="text-[#F4E4C1] text-lg mb-6 line-clamp-4" data-testid="philosophy-preview">
                {aboutPreview.philosophy?.substring(0, 300)}...
              </p>
              <Link to="/about" className="inline-flex items-center retro-button" data-testid="read-full-about">
                <EditableText as="span" id="home.approach.button">Read Full Philosophy</EditableText> <ChevronRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Quick Links */}
      <section className="py-16 bg-[#6B4423]" data-testid="quick-links-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl retro-title text-center mb-12">Explore</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/student-blog" className="cassette-card p-6 hover:scale-105 transition-transform" data-testid="quick-link-blog">
              <h3 className="text-2xl retro-title text-[#3D2817] mb-3">Student Blog</h3>
              <p className="text-[#3D2817]">Follow my journey learning acupuncture and TCM</p>
            </Link>
            <Link to="/patient-videos" className="cassette-card p-6 hover:scale-105 transition-transform" data-testid="quick-link-videos">
              <h3 className="text-2xl retro-title text-[#3D2817] mb-3">Patient Resources</h3>
              <p className="text-[#3D2817]">Nutrition, Qi Gong, and daily wellness tips</p>
            </Link>
            <Link to="/internal-practices" className="cassette-card p-6 hover:scale-105 transition-transform" data-testid="quick-link-practices">
              <h3 className="text-2xl retro-title text-[#3D2817] mb-3">Internal Practices</h3>
              <p className="text-[#3D2817]">Zoom-based school of internal arts</p>
            </Link>
          </div>
        </div>
      </section>

      {/* First Quote - Hero Style */}
      <PageQuote pageName="home" position={1} variant="hero" />

      {/* Mailing List Signup */}
      <MailingListSignup />

      {/* Second Quote - Compact */}
      <PageQuote pageName="home" position={2} variant="compact" />
    </Layout>
  );
}